import { ILanguageCode } from '../../interfaces/languageCode';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { TEXTS } from '../../core/texts';
import { Validator } from '../../core/Validator';
import { BaseObj } from '../../types/baseObj';
import { ValidationErrorState } from '../../types/validationErrorState';
import { SyntheticEvent, useCallback, useMemo } from 'react';
import { useGetLanguageCodes } from '../../queryHooks/useGetLanguageCodes';

interface IProps {
  list?: ILanguageCode[],
  languageCode: string;
  languageCodeNamespace?: string;
  onChange: (val: BaseObj) => void;
  validationErrors?: ValidationErrorState;
}

const LanguageAutocomplete = ({
                                list,
                                languageCode,
                                languageCodeNamespace = 'languageCode',
                                onChange,
                                validationErrors
                              }: IProps) => {

  const { data: languageCodesData } = useGetLanguageCodes({
    enabled: !list,
  });
  
  const codes = useMemo(() => list || languageCodesData || [], [list, languageCodesData]);

  const selectedCode = useMemo(() => codes.find(c => c.code === languageCode) || null,
    [languageCode, codes]);

  const handleCodeChange = useCallback((e: SyntheticEvent<Element, Event>) => {
    if ((e as any)?.code !== 'Enter') {
      const name = e.currentTarget.textContent?.trim();
      const code = codes.find(c => c.name === name);
      onChange({ [languageCodeNamespace]: code?.code || '' });
    }
  }, [codes, languageCodeNamespace, onChange]);

  const handleCodePressEnter = useCallback((e: SyntheticEvent<Element, Event>, val: string) => {
    if ((e as any)?.code === 'Enter') {
      const code = codes.find(c => c.name === val.trim());
      onChange({ [languageCodeNamespace]: code?.code || '' });
    }
  }, [languageCodeNamespace, onChange, codes]);

  return (
    <Autocomplete
      options={codes}
      getOptionLabel={(option: ILanguageCode) => option.name}
      blurOnSelect={true}
      onInputChange={handleCodePressEnter}
      onChange={handleCodeChange}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      value={selectedCode}
      sx={{ width: '100%' }}
      renderOption={(props, option) => (
        <Typography {...props}> {option.name} </Typography>
      )}
      renderInput={(params) => (
        <TextField {...params}
                   label={TEXTS.LANGUAGE}
                   inputProps={{ ...params.inputProps, autoComplete: 'none' }}
                   error={validationErrors && !!validationErrors[languageCodeNamespace]}
                   helperText={validationErrors && validationErrors[languageCodeNamespace]}
                   required={validationErrors && !!Validator.LENGTHS.LANGUAGE[languageCodeNamespace].min}
                   variant={'standard'}
        />
      )}
    />
  );
};

export default LanguageAutocomplete;